import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Spinner, } from 'react-bootstrap';
import TransactionService from '../../../service/TransactionService';
import VerticalLabelList from '../../label/VerticalLabelList';
import FieldSet from '../../fieldset/FieldSet';
import Grid from '../../grid/Grid';
import ObjectUtils from '../../../modules/ObjectUtils';
import TransactionNotes from './TransactionNotes';
import PaymentGatewayService from '../../../service/PaymentGatewayService';
import ServiceOfferingService from '../../../service/ServiceOfferingService';

class DetailedTransaction extends React.Component {
  constructor(props) {
    super(props);
    const { transaction, } = props;
    this.state = {
      transaction,
      serviceOfferingDetailHeaders: [],
      serviceOfferingDetailRows: [],
      paymentHistoryHeaders: [],
      paymentHistoryRows: [],
      lineItemHeaders: [],
      lineItemRows: [],
      isReady: false,
    };
    this.v3KeyExclusions = [ 'paymentHistory', 'lineItems', 'transactionNotes', ];
  }

  componentDidMount() {
    this._mounted = true;
    const { transaction, } = this.props;
    TransactionService.getV3TransactionDetail(transaction.id)
      .then((response) => {
        const { data = {}, } = response;
        if (!data.v3transaction) {
          throw new Error('No V3 Transaction found');
        }
        if (data.v3transaction?.digitalWallet) {
          const criteria = { paymentAccountId: data.v3transaction.paymentAccountId, };
          return PaymentGatewayService.paymentAccount.getPaymentAccountById(criteria)
            .then(result => ({
              ...data,
              v3transaction: {
                ...data.v3transaction,
                digitalWalletProvider: result.data.paymentAccount.digitalWalletInfo.provider,
              },
            }))
            .catch(err => {
              console.error(err);
              return data;
            });
        }
        return data;
      })
      .then((data) => {
        const lineItems = data.v3transaction.lineItems;

        this.populateServiceOfferingDetails(lineItems);

        const paymentHistory = ObjectUtils.formatStringAsJSONArray(
          data.v3transaction.paymentHistory
        );
        const formattedPaymentHistory = [];
        paymentHistory.forEach((item) => {
          formattedPaymentHistory.push({
            ...item,
            timeStamp: new Date(item.timeStamp * 1000).toISOString(),
          });
        });
        const formattedLineItems = lineItems;
        const lineItemRows = [];
        let lineItemHeaders = [];

        const paymentHistoryModel = Grid.toModelFromList(formattedPaymentHistory, {
          headerFormatter: value => _.startCase(value.header),
        });

        lineItemHeaders = ObjectUtils.toLargestKeySet(formattedLineItems).map(lineItem => ({
          display: _.startCase(lineItem),
          value: lineItem,
        }));

        formattedLineItems.map((lineItem) => {
          const item = lineItem;
          item.amount = lineItem.amount;

          if (lineItem.fees !== undefined) {
            if (lineItem.fees.PAYIT_FEE !== undefined) {
              item.fees.PAYIT_FEE = lineItem.fees.PAYIT_FEE.value;
            }
            if (lineItem.fees.AGENCY_FEE !== undefined) {
              item.fees.AGENCY_FEE = lineItem.fees.AGENCY_FEE.value;
            }
            if (lineItem.fees.CREDIT_CARD !== undefined) {
              item.fees.CREDIT_CARD = lineItem.fees.CREDIT_CARD.value;
            }
            if (lineItem.fees.DEBIT_CARD !== undefined) {
              item.fees.DEBIT_CARD = lineItem.fees.DEBIT_CARD.value;
            }
            if (lineItem.fees.ACH !== undefined) {
              item.fees.ACH = lineItem.fees.ACH.value;
            }
          }

          return item;
        });

        formattedLineItems.forEach((lineItem) => {
          const values = Object.values(lineItem);

          lineItemRows.push({
            value: values,
            raw: lineItem,
          });
        });

        if (this._mounted) {
          this.setState({
            transaction: data.v3transaction,
            paymentHistoryHeaders: paymentHistoryModel.headers,
            paymentHistoryRows: paymentHistoryModel.format(formattedPaymentHistory),
            lineItemHeaders,
            lineItemRows,
            isReady: true,
          });
        }
      })
      .catch((err) => {
        console.error(`Error parsing V3 Transaction Detail: ${err}`);
      });
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  async populateServiceOfferingDetails(lineItems) {
    const serviceOfferingDetailHeaders = [];
    const serviceOfferingDetailRows = await this.getServiceOfferingDetailRows(lineItems);
    const relevantDetails = [ 'id', 'displayName', 'created', 'description', 'active', 'zoneId', ];

    relevantDetails.forEach((detail) => {
      serviceOfferingDetailHeaders.push({
        display: _.startCase(detail),
        value: detail,
      });
    });

    this.setState({
      serviceOfferingDetailHeaders,
      serviceOfferingDetailRows,
    });
  }

  async getServiceOfferingDetailRows(lineItems) {
    const serviceOfferingDetailRows = [];

    const serviceOfferingDetailPromises = lineItems.map(lineItem => (
      ServiceOfferingService.getServiceById({ id: lineItem.serviceOfferingId, })
        .then((sosResponse) => {
          const serviceOfferingDetailData = sosResponse.data.service.data;
          const serviceOfferingDetailAttributes = sosResponse.data.service.data.attributes;
          const customServiceOfferingDetails = {
            id: serviceOfferingDetailData.id || 'Not found',
            displayName: serviceOfferingDetailAttributes.displayName || 'Not found',
            created: serviceOfferingDetailAttributes.created || 'Not found',
            description: serviceOfferingDetailAttributes.description || 'Not found',
            active: serviceOfferingDetailAttributes.active || 'Not found',
            zoneId: serviceOfferingDetailAttributes.zoneId || 'Not found',
          };

          serviceOfferingDetailRows.push({
            value: Object.values(customServiceOfferingDetails),
            raw: customServiceOfferingDetails,
          });
        }).catch(err => {
          console.error(err);
          serviceOfferingDetailRows.push({
            value: [ 'Error fetching service offering details', ],
            raw: { 'Error fetching service offering details': 'Error fetching service offering details', },
          });
        })
    ));

    await Promise.all(serviceOfferingDetailPromises);

    return serviceOfferingDetailRows;
  }

  formatTransaction(transaction) {
    const copy = { ...transaction, };

    copy.achData = transaction.achData;
    copy.fees_ACH = transaction.fees_ACH;
    copy.fees_AGENCY_FEE = transaction.fees_AGENCY_FEE;
    copy.fees_PAYIT_FEE = transaction.fees_PAYIT_FEE;
    copy.fees_CREDIT_CARD = transaction.fees_CREDIT_CARD;
    copy.fees_DEBIT_CARD = transaction.fees_DEBIT_CARD;
    copy.fees_CASH = transaction.fees_CASH;
    copy.cashStatus = transaction.cashData;
    copy.responseCode = transaction.ccData_processorResponse?.responseCode;
    copy.responseMessage = transaction.ccData_processorResponse?.responseMessage;
    copy.refundingTransactionIds = transaction.refundingTransactionIds;

    copy.refundingTransactionIds?.forEach((id, index) => {
      copy[`refundingTransactionId${index + 1}`] = id;
    });
    delete copy.ccData_processorResponse;
    delete copy.refundingTransactionIds;
    return copy;
  }

  render() {
    const {
      transaction = {},
      paymentHistoryHeaders = [],
      paymentHistoryRows = [],
      lineItemHeaders = [],
      lineItemRows = [],
      serviceOfferingDetailHeaders = [],
      serviceOfferingDetailRows = [],
      isReady,
    } = this.state;
    const formatTransaction = this.formatTransaction(transaction);

    let items;

    if (isReady) {
      items = VerticalLabelList.toList(formatTransaction, {
        displayFormatter: (value) => {
          const formattedValue = value.split('_').join(' ');
          return _.startCase(formattedValue);
        },
      });
    }

    return (
      <>
        {(() => {
          if (isReady) {
            return (
              <>
                <FieldSet title="Transaction Summary" style={{ maxHeight: '400px', }}>
                  <VerticalLabelList items={items} exclusions={this.v3KeyExclusions} />
                </FieldSet>
                <>
                  <FieldSet title="Line Items">
                    <Grid headers={lineItemHeaders} rows={lineItemRows} />
                  </FieldSet>
                </>
                <FieldSet title="Payment History">
                  <Grid headers={paymentHistoryHeaders} rows={paymentHistoryRows} />
                </FieldSet>
                <FieldSet title="Service Offering Details">
                  <Grid headers={serviceOfferingDetailHeaders} rows={serviceOfferingDetailRows} />
                </FieldSet>
                <TransactionNotes
                  transactionNotes={ObjectUtils.formatStringAsJSONArray(
                    transaction.transactionNotes
                  )}
                />
              </>
            );
          }
          return (
            <div className="row justify-content-center">
              <Spinner animation="border" />
            </div>
          );
        })()}
      </>
    );
  }
}

DetailedTransaction.defaultProps = {};

DetailedTransaction.propTypes = {
  transaction: PropTypes.instanceOf(Object).isRequired,
};

export default DetailedTransaction;
